import { format, parse, isValid, setMinutes, setHours } from "date-fns";
const convertToDate = (date) => date instanceof Date ? date : typeof date === "string" ? parseStandardDate(date) : void 0;
const STANDARD_DATE_FORMAT = "yyyy-MM-dd";
const STANDARD_DATE_TIME_FORMAT = `yyyy-MM-dd'T'HH:mm:ss`;
const formatStandardDate = (date) => format(date, STANDARD_DATE_FORMAT);
const parseStandardDate = (date) => parse(date, STANDARD_DATE_FORMAT, /* @__PURE__ */ new Date());
const getLocaleDateFormat = (locale) => {
  const date = new Date(2e3, 0, 1);
  const formatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const parts = formatter.formatToParts(date);
  let dateFormat = "";
  for (const part of parts) {
    switch (part.type) {
      case "day":
        dateFormat += "dd";
        break;
      case "month":
        dateFormat += "MM";
        break;
      case "year":
        dateFormat += "yyyy";
        break;
      default:
        dateFormat += part.value;
    }
  }
  return dateFormat;
};
const formatDateRange = (value, formatString, rangeDivider) => {
  const [fromDate, toDate] = value;
  return `${fromDate ? format(fromDate, formatString) : ""}${toDate ? `${rangeDivider}${format(toDate, formatString)}` : ""}`;
};
const DATE_RANGE_DELIMITER = " - ";
const formatLocaleDateRange = (value, locale) => formatDateRange(value, getLocaleDateFormat(locale), DATE_RANGE_DELIMITER);
const parseDateRange = (value, formatString, rangeDivider) => {
  return parseDateRangeInternal(
    value,
    rangeDivider,
    (dateString) => parse(dateString, formatString, /* @__PURE__ */ new Date())
  );
};
const parseDateRangeInternal = (value, rangeDivider, parser) => {
  if (!value) {
    return [];
  }
  const [fromVal, toVal] = value.split(rangeDivider);
  const fromDate = fromVal ? parser(fromVal) : void 0;
  const toDate = toVal ? parser(toVal) : void 0;
  const range = !fromDate && !toDate ? [] : fromDate && !toDate ? [fromDate] : [fromDate, toDate];
  return range;
};
const rangeHasChanged = (oldRange, newRange) => {
  if (!oldRange && !newRange) {
    return false;
  }
  if (!oldRange || !newRange) {
    return true;
  }
  const [oldFrom, oldTo] = oldRange;
  const [newFrom, newTo] = newRange;
  const fromDatesEqual = !oldFrom && !newFrom || !!oldFrom && !!newFrom && oldFrom.getTime() === newFrom.getTime();
  const toDatesEqual = !oldTo && !newTo || !!oldTo && !!newTo && oldTo.getTime() === newTo.getTime();
  return !fromDatesEqual || !toDatesEqual;
};
const MIN_DATE = new Date(1900, 0, 1);
const MAX_DATE = new Date(2100, 0, 1);
const isDateInRange = (date, minDate, maxDate) => {
  minDate = minDate < MIN_DATE ? MIN_DATE : minDate;
  maxDate = maxDate > MAX_DATE ? MAX_DATE : maxDate;
  return date >= minDate && date <= maxDate;
};
const isDateRangeValid = (range, mode, minDate = MIN_DATE, maxDate = MAX_DATE) => {
  const [fromDate, toDate] = range;
  if (!fromDate && !toDate) {
    return true;
  }
  if (mode === "single") {
    return !!fromDate && !toDate && isValid(fromDate) && isDateInRange(fromDate, minDate, maxDate);
  }
  return !!fromDate && !!toDate && isValid(fromDate) && isValid(toDate) && isDateInRange(fromDate, minDate, maxDate) && isDateInRange(toDate, minDate, maxDate) && toDate >= fromDate;
};
const normalizeLocale = (rawLocale) => {
  let locale = (rawLocale == null ? void 0 : rawLocale.trim()) || navigator.language;
  try {
    locale = Intl.getCanonicalLocales(locale)[0];
  } catch {
    locale = navigator.language;
  }
  return locale;
};
const setTimeInDate = (date, time) => {
  if (date) {
    const parts = time.split(":").map(Number);
    const hour = parts[0] || 0;
    const minute = parts[1] || 0;
    return setMinutes(setHours(date, hour), minute);
  }
  return date;
};
const parseTimeFromDate = (date) => {
  return date ? format(date, "HH:mm") : "";
};
export {
  DATE_RANGE_DELIMITER,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  convertToDate,
  formatDateRange,
  formatLocaleDateRange,
  formatStandardDate,
  getLocaleDateFormat,
  isDateRangeValid,
  normalizeLocale,
  parseDateRange,
  parseDateRangeInternal,
  parseStandardDate,
  parseTimeFromDate,
  rangeHasChanged,
  setTimeInDate
};
